import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { PageHead, Skel, Empty, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Usage metering (parts 130/171). The server owns the catalogue and the
 * effective limits; this page only renders them, so adding a metric needs no
 * web change.
 */

type QuotaStatus = 'allowed' | 'soft_warn' | 'blocked'

interface UsageRow {
  metric: string
  label_th: string
  unit_th: string
  hint_th: string
  used: number
  limit_value: number
  percent: number
  status: QuotaStatus
}

interface UsageEvent {
  id: number
  metric: string
  label_th: string
  qty: number
  ref: string
  created_at: string
}

interface UsageSnapshot {
  period: string
  plan_id: string
  plan_limits: Record<string, number>
  metrics: UsageRow[]
  events: UsageEvent[]
  updated_at?: string
}

const statusLabel: Record<QuotaStatus, string> = {
  allowed: 'ปกติ',
  soft_warn: 'ใกล้เต็ม',
  blocked: 'ครบแล้ว',
}

const statusTone: Record<QuotaStatus, string> = {
  allowed: 'var(--color-gold)',
  soft_warn: 'var(--color-amber)',
  blocked: 'var(--color-crimson)',
}

const planLabel: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
  enterprise: 'Enterprise',
}

/** Current billing month in Thailand time, formatted YYYY-MM. */
function currentPeriod(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit',
  }).formatToParts(new Date())
  const year = parts.find((p) => p.type === 'year')?.value ?? '2026'
  const month = parts.find((p) => p.type === 'month')?.value ?? '01'
  return `${year}-${month}`
}

export function Usage() {
  const now = useMemo(currentPeriod, [])
  const [period, setPeriod] = useState(now)

  const usage = useQuery({
    queryKey: ['usage', period],
    queryFn: () => api<UsageSnapshot>(`/v1/usage?period=${encodeURIComponent(period)}`),
    staleTime: 15_000,
  })

  const snap = usage.data
  const rows = snap?.metrics ?? []
  const events = snap?.events ?? []

  const summary = useMemo(() => {
    let warn = 0
    let blocked = 0
    let peak = 0
    for (const r of rows) {
      if (r.status === 'soft_warn') warn += 1
      if (r.status === 'blocked') blocked += 1
      if (r.limit_value > 0 && r.percent > peak) peak = r.percent
    }
    return { warn, blocked, peak }
  }, [rows])

  return (
    <>
      <PageHead
        title="การใช้บริการและโควตา"
        sub="วัดการใช้งานรายเดือนแยกตามตัวชี้วัด · เตือนเมื่อใช้ถึง 80% · ระงับเมื่อครบ 100%"
        actions={
          <input
            className="input"
            type="month"
            aria-label="รอบบิล"
            value={period}
            max={now}
            onChange={(e) => setPeriod(e.target.value || now)}
            style={{ maxWidth: 180 }}
          />
        }
      />

      {usage.isLoading ? (
        <div className="stack"><Skel h={120} /><Skel h={320} /></div>
      ) : usage.isError ? (
        <div className="card card-pad">
          <div className="form-error">
            {usage.error instanceof Error ? usage.error.message : 'โหลดข้อมูลการใช้บริการไม่สำเร็จ'}
          </div>
        </div>
      ) : !snap ? null : (
        <>
          <div className="grid-4 stagger">
            <Stat label="แพ็กเกจที่ใช้คิดโควตา" value={planLabel[snap.plan_id] ?? snap.plan_id}
              foot={`รอบ ${snap.period}`} />
            <Stat label="โควตาสูงสุดที่ใช้ไป" value={`${summary.peak}%`}
              foot="เทียบกับเพดานของแต่ละตัวชี้วัด" />
            <Stat label="ตัวชี้วัดใกล้เต็ม" value={summary.warn} unit="รายการ"
              foot="ตั้งแต่ 80% ขึ้นไปแต่ยังไม่ถึงเพดาน" />
            <Stat label="ตัวชี้วัดที่ครบโควตา" value={summary.blocked} unit="รายการ"
              foot="การเขียนใหม่จะถูกปฏิเสธจนกว่ารอบจะรีเซ็ต" />
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row">
              <span className="card-label">โควตาตามตัวชี้วัด</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                ตัวเลขที่แสดงคือเพดานที่มีผลจริง รวมค่าที่ผู้ดูแลตั้งไว้
              </span>
            </div>

            {rows.length === 0 ? (
              <div style={{ marginTop: 14 }}>
                <Empty title="ยังไม่มีตัวชี้วัด" body="ยังไม่มีการกำหนดโควตาสำหรับองค์กรนี้" />
              </div>
            ) : (
              <div className="stack" style={{ marginTop: 18, gap: 22 }}>
                {rows.map((m) => {
                  const unlimited = m.limit_value <= 0
                  const width = unlimited ? 100 : Math.max(m.used > 0 ? 2 : 0, Math.min(100, m.percent))
                  return (
                    <div key={m.metric}>
                      <div className="row" style={{ alignItems: 'baseline', gap: 10 }}>
                        <span style={{ fontSize: 13 }}>{m.label_th}</span>
                        <span
                          className="chip"
                          style={{ color: statusTone[m.status], borderColor: statusTone[m.status] }}
                        >
                          {statusLabel[m.status]}
                        </span>
                        <span className="dim tnum" style={{ marginLeft: 'auto', fontSize: 12 }}>
                          {number(m.used)} / {unlimited ? 'ไม่จำกัด' : number(m.limit_value)} {m.unit_th}
                        </span>
                        <span className="tnum" style={{ minWidth: 42, textAlign: 'right', fontSize: 12 }}>
                          {unlimited ? '—' : `${m.percent}%`}
                        </span>
                      </div>
                      <div className="bar" style={{ height: 6, marginTop: 8 }}>
                        <i style={{ width: `${width}%`, background: unlimited ? 'var(--color-hairline-strong)' : statusTone[m.status] }} />
                      </div>
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>{m.hint_th}</div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="grid-main" style={{ marginTop: 16 }}>
            <div className="card card-pad">
              <div className="card-label">บันทึกเหตุการณ์ล่าสุด</div>
              {events.length === 0 ? (
                <div style={{ marginTop: 14 }}>
                  <Empty title="ยังไม่มีเหตุการณ์ในรอบนี้" body="เมื่อมีการใช้งานที่ถูกวัดผล รายการจะปรากฏที่นี่" />
                </div>
              ) : (
                <div className="table-wrap" style={{ marginTop: 12 }}>
                  <table className="table data">
                    <thead>
                      <tr>
                        <th>เวลา</th><th>ตัวชี้วัด</th><th style={{ textAlign: 'right' }}>จำนวน</th><th>อ้างอิง</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((e) => (
                        <tr key={e.id} style={{ cursor: 'default' }}>
                          <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDateTime(e.created_at)}</td>
                          <td>{e.label_th || e.metric}</td>
                          <td className="num tnum">{number(e.qty)}</td>
                          <td className="mono dim" title={e.ref} style={{ whiteSpace: 'nowrap' }}>
                            {e.ref ? (e.ref.length > 24 ? `${e.ref.slice(0, 24)}…` : e.ref) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card card-pad">
              <div className="card-label">วิธีคิดโควตา</div>
              <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
                <li>ทุกตัวชี้วัดนับแยกรอบเดือนตามเวลาไทย และแยกตามองค์กร</li>
                <li>ใช้ถึง 80% ของเพดานจะขึ้นสถานะ “ใกล้เต็ม” แต่ยังทำงานได้ตามปกติ</li>
                <li>เมื่อใช้ครบ 100% ระบบตอบรหัส QUOTA_EXCEEDED และไม่บันทึกงานใหม่</li>
                <li>การเขียนที่ล้มเหลวไม่ถูกนับ เพราะบันทึกการใช้หลังคำขอสำเร็จเท่านั้น</li>
                <li>ผู้ดูแลสามารถตั้งเพดานเฉพาะองค์กรได้ ค่า 0 หมายถึงใช้ค่าแพ็กเกจ</li>
              </ul>
              {snap.updated_at ? (
                <div className="dim" style={{ fontSize: 11.5, marginTop: 12 }}>
                  อัปเดตล่าสุด {thaiDateTime(snap.updated_at)}
                </div>
              ) : null}
              <div className="dim" style={{ fontSize: 11.5, marginTop: 12 }}>
                แสดงเหตุการณ์ล่าสุดไม่เกิน 200 รายการในรอบที่เลือก
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
